import React, { Component } from 'react';
import './App.css';
import { Container } from 'reactstrap';
import { Switch, Route } from 'react-router-dom';
import routes from 'routes';
import { Helmet } from 'react-helmet';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'react-router-redux';
import styled, {
  css,
  createGlobalStyle,
  ThemeProvider
} from 'styled-components';
import { getTheme, getThemeName } from 'utils/themeUtils';
import { getBookingSource, HOST_BOOKING } from 'utils/routeUtils';
import { IntlProvider } from 'react-intl';
import AnimateRoutesContainer from 'containers/AnimateRoutesContainer';
import ErrorBoundary from 'react-error-boundary';
import ErrorFallback from 'components/ErrorFallback';
import ConfirmationGrid from 'routes/Confirmation/components/styled/ConfirmationGrid';
import Promotions from 'routes/Confirmation/components/styled/Promotions';
import { ApolloProvider } from 'react-apollo';
import GAListener from 'components/GAListener';
import MaintenanceMode from 'components/MaintenanceMode';
import { THEME_HOST_BOOKING } from 'themes';
import { ifProp } from 'styled-tools';

const GlobalStyle = createGlobalStyle`
  .theme-wcc {

    ${ConfirmationGrid} {
      display: flex;
      flex-direction: column;
      height: 100%;

      ${Promotions} {
        height: 100%;
      }

      & > .row {
        margin: 0;
      }

      .slick-slider {
        max-height: 60%;
        flex: 0 1 auto;
      }
    }
  }

  @media print {
    body *:not(.printable) {
      position: initial;
    }

    body {
      background: transparent;
    }

    .promotions-col {
      display: none;
    }

    .booking-confirmation {
      position: absolute;
      top: 2em;
      left: 0;
      right: 0;
      background-color: transparent;
      font-size: 2em;
    }
  }

  .slide-enter {
    margin-right: -100%;
  }

  .slide-enter.slide-enter-active {
    margin-right: 0;
    transition: margin-right 500ms ease-in-out;
  }

  .slide-enter-done {
    height: 100%;
    /* Required to prevent white screen after back transition completed */
    opacity: 1 !important;
  }

  .slide-exit {
    opacity: 1
  }

  .slide-exit.slide-exit-active {
    opacity: 0;
    transition: opacity 500ms cubic-bezier(1, 0, 1, 0);
  }

  .back-enter {
    margin-left: -100%;
  }

  .back-enter.back-enter-active {
    margin-left: 0;
    transition: margin-left 500ms ease-in-out;
  }

  .back-enter-done {
    &:not(.row) {
      margin-left: 0;
      margin-right: 0;
    }

    /* Required to prevent white screen after transition completed */
    opacity: 1 !important;
  }

  .back-exit {
    opacity: 1
  }

  .back-exit.back-exit-active {
    opacity: 0;
    transition: opacity 500ms cubic-bezier(1, 0, 1, 0);
  }

  .animate-routes-container > div {
    display: flex;
    flex-direction: row-reverse;
    flex-wrap: nowrap;

    & > * {
      flex: 1 0 auto;
      width: 100%;
      height: 100%;
    }
  }

  .animate-routes-container.back > div {
    flex-direction: row;
  }
`;

const StyledApp = styled.div`
  padding-top: 1.5em;
  height: 100%;
  min-height: 18.75em;

  ${ifProp(
    { themeName: THEME_HOST_BOOKING },
    css`
      padding-top: 0;

      .App {
        overflow-y: hidden;
      }
    `
  )}

  .animate-routes-container {
    height: 100%;

    & > div,
    & > div > .row {
      height: 100%;
    }
  }

  .maintenance-mode {
    height: 100%;
  }

  a {
    color: ${props => props.theme.a.color};
    font-weight: bold;
    text-decoration: underline;
    font-size: 0.9rem;
  }

  h5 {
    color: ${props => props.theme.h5.color};
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.large}) {
    .App:not(.theme-wcc) {
      & > .container-fluid {
        height: 100%;
      }
    }
  }

  @media screen and (max-width: ${props => props.theme.breakpoints.medium}) {
    padding-top: 0;
  }

  .theme-wcc {
    .back-button-bottom {
      display: none;
    }
    .back-button-middle {
      display: inline;
    }
  }
`;

class App extends Component {
  render() {
    const { store, history, apolloClient } = this.props;

    // Get the theme name from query parameter "theme", or use default theme.
    const themeName = getThemeName(history.location);
    const bookingSource = getBookingSource(history.location);
    const appClasses = [
      'App',
      `theme-${themeName}`,
      bookingSource === HOST_BOOKING ? ' host-booking' : ''
    ]
      .join(' ')
      .trim();

    return (
      <ApolloProvider client={apolloClient}>
        <Provider store={store}>
          <IntlProvider locale={'en'}>
            <ConnectedRouter history={history}>
              <GAListener
                trackingId={process.env.REACT_APP_GOOGLE_ANALYTICS_ID}
                bethlehemFloodlightId={
                  process.env.REACT_APP_GOOGLE_FLOODLIGHT_ID
                }
                themeName={themeName}
              >
                <Route
                  render={({ location }) => (
                    <ThemeProvider theme={getTheme(themeName)}>
                      <React.Fragment>
                        <GlobalStyle />
                        <StyledApp themeName={themeName}>
                          <div className={appClasses} role="main">
                            <Helmet titleTemplate="%s | Wind Creek">
                              <title>Booking Engine</title>
                            </Helmet>
                            <Container fluid>
                              <MaintenanceMode>
                                <ErrorBoundary
                                  FallbackComponent={ErrorFallback}
                                >
                                  <AnimateRoutesContainer location={location}>
                                    <Switch location={location}>
                                      {routes}
                                      <Route
                                        render={() => <div>Not Found</div>}
                                      />
                                    </Switch>
                                  </AnimateRoutesContainer>
                                </ErrorBoundary>
                              </MaintenanceMode>
                            </Container>
                          </div>
                        </StyledApp>
                      </React.Fragment>
                    </ThemeProvider>
                  )}
                />
              </GAListener>
            </ConnectedRouter>
          </IntlProvider>
        </Provider>
      </ApolloProvider>
    );
  }
}

export default App;
